import { useState } from "react";
import { useLang } from "../i18n/LangContext";

// Detects http(s):// and www. URLs. Trailing punctuation is excluded from the match.
const URL_RE = /((?:https?:\/\/|www\.)[^\s<]+[^\s<.,;:!?)\]}'"])/gi;

function safeHref(raw) {
  const candidate = raw.startsWith("www.") ? `https://${raw}` : raw;
  try {
    const u = new URL(candidate);
    if (u.protocol === "http:" || u.protocol === "https:") return u.href;
  } catch {
    /* invalid URL → not linkified */
  }
  return null; // rejects javascript:, data:, etc.
}

function tokenize(text) {
  const parts = [];
  let last = 0;
  let m;
  URL_RE.lastIndex = 0;
  while ((m = URL_RE.exec(text)) !== null) {
    if (m.index > last) parts.push({ type: "text", value: text.slice(last, m.index) });
    parts.push({ type: "url", value: m[0] });
    last = m.index + m[0].length;
  }
  if (last < text.length) parts.push({ type: "text", value: text.slice(last) });
  return parts;
}

// Renders user caption as PLAIN TEXT (never dangerouslySetInnerHTML). Newlines are preserved via
// pre-wrap; only valid http/https/www tokens become safe external links. `clamp` (line count)
// enables a "Selengkapnya" toggle for long captions in the feed.
export function LinkifiedText({ text, className = "", clamp = 0, testId }) {
  const { t } = useLang();
  const [expanded, setExpanded] = useState(false);
  if (!text) return null;

  const lines = text.split("\n");
  const isLong = clamp > 0 && (lines.length > clamp || text.length > 400);
  let shown = text;
  if (isLong && !expanded) {
    shown = lines.slice(0, clamp).join("\n");
    if (shown.length > 400) shown = shown.slice(0, 400);
  }

  return (
    <div data-testid={testId} className={className}>
      <p className="whitespace-pre-wrap break-words leading-relaxed">
        {tokenize(shown).map((p, i) => {
          if (p.type === "url") {
            const href = safeHref(p.value);
            if (href) {
              return (
                <a key={i} href={href} target="_blank" rel="noopener noreferrer nofollow"
                  onClick={(e) => e.stopPropagation()}
                  className="text-sky-400 hover:text-sky-300 underline underline-offset-2 break-all">
                  {p.value}
                </a>
              );
            }
          }
          return <span key={i}>{p.value}</span>;
        })}
        {isLong && !expanded && "…"}
      </p>
      {isLong && (
        <button type="button" data-testid={testId ? `${testId}-toggle` : undefined}
          onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
          className="mt-1 text-xs font-semibold text-slate-400 hover:text-white">
          {expanded ? t("food.showLess") : t("food.readMore")}
        </button>
      )}
    </div>
  );
}
